import React from "react";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { fetchCandidates, ICandidateFilter } from "../../data/Candidates";
import { useQuery } from "@tanstack/react-query";
import CandidateListFilter from "./CandidateListFilter";
import { IAddress, addressFields } from "../../data/Address";
import TableToolbar from "../misc/TableToolbar";
import CandidateListTable from "./CandidateListTable";

export default function CandidateListPage() {
  const { enqueueSnackbar } = useSnackbar();

  React.useEffect(() => {
    document.title = "Candidates";
  }, []);

  const { control, handleSubmit, reset } = useForm<ICandidateFilter>({
    defaultValues: {
      search: "",
      nationality: null,
      location: null,
      experience: [],
      technical_skills: [],
      levels: [],
      specialties: [],
      languages: [],
      areas: [],
      qualifications: [],
      driving_licence: null,
      max_notice_period: 12,
      status: null,
      salary_min: "",
      salary_max: "",
      permanent_resident: false,
      qualifies_for_bee: false,
      valid_work_permit: false,
    },
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const filter = React.useMemo(() => {
    const locationFields = Object.fromEntries(
      addressFields.map((field) => [
        field,
        searchParams.get(field) ?? undefined,
      ])
    );
    const locationDefined = Object.values(locationFields).some(
      (val) => val !== undefined
    );
    const location = locationDefined ? (locationFields as IAddress) : null;

    const maximumNoticePeriod = Number(
      searchParams.get("max_notice_period") ?? 12
    );
    const nationality = searchParams.get("nationality");
    const drivingLicence = searchParams.get("driving_licence");
    const status = searchParams.get("status");

    return {
      search: searchParams.get("search") ?? "",
      nationality: nationality ? Number(nationality) : null,
      location,
      experience: searchParams.getAll("experience").map(Number),
      technical_skills: searchParams.getAll("technical_skills").map(Number),
      levels: searchParams.getAll("levels").map(Number),
      specialties: searchParams.getAll("specialties").map(Number),
      languages: searchParams.getAll("languages").map(Number),
      areas: searchParams.getAll("areas").map(Number),
      qualifications: searchParams.getAll("qualifications").map(Number),
      driving_licence: drivingLicence ? Number(drivingLicence) : null,
      max_notice_period: Number.isNaN(maximumNoticePeriod)
        ? 12
        : maximumNoticePeriod,
      status: status ? Number(status) : null,
      salary_min: searchParams.get("salary_min") ?? "",
      salary_max: searchParams.get("salary_max") ?? "",
      permanent_resident: searchParams.get("permanent_resident") === "true",
      qualifies_for_bee: searchParams.get("qualifies_for_bee") === "true",
      valid_work_permit: searchParams.get("valid_work_permit") === "true",
    } as ICandidateFilter;
  }, [searchParams]);

  React.useEffect(() => {
    reset(filter);
  }, [filter, reset]);

  const {
    data: candidates,
    error,
    isLoading: candidatesLoading,
  } = useQuery({
    queryKey: ["candidates", searchParams.toString()],
    queryFn: () => fetchCandidates(searchParams.toString()),
  });

  React.useEffect(() => {
    if (error) {
      enqueueSnackbar("Failed to load candidates.", { variant: "error" });
    }
  }, [error, enqueueSnackbar]);

  const handleFilter = (data: ICandidateFilter) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("search");
      if (data.search) params.set("search", data.search);
      params.delete("nationality");
      if (data.nationality)
        params.set("nationality", data.nationality.toString());
      addressFields.forEach((field) => {
        params.delete(field);
      });
      if (data.location) {
        const location = data.location as Record<string, string | undefined>;
        addressFields.forEach((field) => {
          const value = location[field];
          if (value) {
            params.set(field, value);
          }
        });
      }
      params.delete("experience");
      data.experience.forEach((experience) => {
        params.append("experience", experience.toString());
      });
      params.delete("technical_skills");
      data.technical_skills.forEach((skill) => {
        params.append("technical_skills", skill.toString());
      });
      params.delete("levels");
      data.levels.forEach((level) => {
        params.append("levels", level.toString());
      });
      params.delete("specialties");
      data.specialties.forEach((specialty) => {
        params.append("specialties", specialty.toString());
      });
      params.delete("languages");
      data.languages.forEach((language) => {
        params.append("languages", language.toString());
      });
      params.delete("areas");
      data.areas.forEach((area) => {
        params.append("areas", area.toString());
      });
      params.delete("qualifications");
      data.qualifications.forEach((qualification) => {
        params.append("qualifications", qualification.toString());
      });
      params.delete("driving_licence");
      if (data.driving_licence)
        params.set("driving_licence", data.driving_licence.toString());
      params.delete("max_notice_period");
      params.set("max_notice_period", data.max_notice_period.toString());
      params.delete("status");
      if (data.status) params.set("status", data.status.toString());
      params.delete("salary_min");
      if (data.salary_min) params.set("salary_min", data.salary_min);
      params.delete("salary_max");
      if (data.salary_max) params.set("salary_max", data.salary_max);
      params.delete("permanent_resident");
      if (data.permanent_resident) params.set("permanent_resident", "true");
      params.delete("qualifies_for_bee");
      if (data.qualifies_for_bee) params.set("qualifies_for_bee", "true");
      params.delete("valid_work_permit");
      if (data.valid_work_permit) params.set("valid_work_permit", "true");
      params.delete("page");
      return params;
    });
  };

  return (
    <Box
      sx={{
        display: "grid",
        borderBottom: 1,
        borderColor: "grey.300",
        gridTemplateColumns: `1fr 320px`,
        gridTemplateAreas: `
      "table filter"
      `,
      }}
    >
      <Box sx={{ gridArea: "table" }}>
        <TableToolbar name="Candidates" create="/candidates/create" />
        <CandidateListTable
          candidates={candidates}
          isLoading={candidatesLoading}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          filter={filter}
        />
      </Box>
      <Box
        component="form"
        onSubmit={handleSubmit(handleFilter)}
        autoComplete="off"
        sx={{ gridArea: "filter", borderLeft: 1, borderColor: "grey.300" }}
      >
        <CandidateListFilter control={control} />
      </Box>
    </Box>
  );
}
